import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { HomeCMS, PageHeroesCMS, RecruitingCMS, ContactCMS, BrandCMS, ProgramsCMS, FAQCMS } from "@/pages/AdminCMS";

const TABS = ["overview", "home", "page-heroes", "programs-cms", "recruiting-cms", "faq-cms", "contact-cms", "brand", "players", "videos", "events", "coaches", "signees", "testimonials", "pricing", "payments", "users", "messages"];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [data, setData] = useState({ players: [], videos: [], events: [], coaches: [], payments: [], users: [], messages: [], signees: [], testimonials: [], memberships: [] });

  const loadAll = async () => {
    try {
      const [s, p, v, ev, co, pay, u, msg, sg, ts, mb] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/players"),
        api.get("/videos"),
        api.get("/events"),
        api.get("/coaches"),
        api.get("/admin/payments"),
        api.get("/admin/users"),
        api.get("/contact"),
        api.get("/signees"),
        api.get("/testimonials"),
        api.get("/memberships"),
      ]);
      setStats(s.data);
      setData({ players: p.data, videos: v.data, events: ev.data, coaches: co.data, payments: pay.data, users: u.data, messages: msg.data, signees: sg.data, testimonials: ts.data, memberships: mb.data });
    } catch { toast.error("Failed to load admin data."); }
  };
  useEffect(() => { loadAll(); }, []);

  const del = async (path, id, key) => {
    if (!confirm("Delete this item?")) return;
    try { await api.delete(`${path}/${id}`); toast.success("Deleted."); loadAll(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="section-surface min-h-screen py-12">
      <div className="container-app">
        <div className="label-tag mb-3" style={{ color: "#000" }}>Admin</div>
        <h1 className="font-heading font-black uppercase text-4xl md:text-6xl leading-none">Command Center.</h1>

        <div className="mt-8 flex gap-2 flex-wrap border-b border-black/10">
          {TABS.map(t => (
            <button key={t} onClick={()=>setTab(t)}
              className={`px-4 py-3 font-heading uppercase text-sm tracking-widest border-b-2 -mb-px ${tab===t?"border-[color:var(--gold)] text-black":"border-transparent text-neutral-500 hover:text-black"}`}
              data-testid={`admin-tab-${t}`}>
              {t.replace(/-cms$/, "").replace(/-/g, " ")}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && <Overview stats={stats} />}
          {tab === "home" && <HomeCMS />}
          {tab === "page-heroes" && <PageHeroesCMS />}
          {tab === "programs-cms" && <ProgramsCMS />}
          {tab === "recruiting-cms" && <RecruitingCMS />}
          {tab === "faq-cms" && <FAQCMS />}
          {tab === "contact-cms" && <ContactCMS />}
          {tab === "brand" && <BrandCMS />}
          {tab === "players" && <Table rows={data.players} cols={["name","position","class_year","school","gpa","recruiting_status"]} onDelete={(id)=>del("/players", id)} testid="admin-players" />}
          {tab === "videos" && <Table rows={data.videos} cols={["title","video_type","url"]} onDelete={(id)=>del("/videos", id)} testid="admin-videos" />}
          {tab === "events" && <EventsAdmin data={data.events} reload={loadAll} />}
          {tab === "coaches" && <CoachesAdmin data={data.coaches} reload={loadAll} />}
          {tab === "signees" && <SigneesAdmin data={data.signees} coaches={data.coaches} reload={loadAll} />}
          {tab === "testimonials" && <TestimonialsAdmin data={data.testimonials} reload={loadAll} />}
          {tab === "pricing" && <PricingAdmin data={data.memberships} reload={loadAll} />}
          {tab === "payments" && <Table rows={data.payments.map(p=>({...p, meta: JSON.stringify(p.metadata||{}).slice(0,40)}))} cols={["kind","item_id","amount","currency","payment_status","user_email"]} testid="admin-payments" />}
          {tab === "users" && <Table rows={data.users} cols={["email","name","role","created_at"]} testid="admin-users" />}
          {tab === "messages" && <Table rows={data.messages} cols={["name","email","subject","message","created_at"]} testid="admin-messages" />}
        </div>
      </div>
    </div>
  );
}

function Overview({ stats }) {
  const items = [
    { label: "Athletes", v: stats.players },
    { label: "Users", v: stats.users },
    { label: "Videos", v: stats.videos },
    { label: "Events", v: stats.events },
    { label: "Paid Txns", v: stats.paid_transactions },
    { label: "Revenue", v: `$${(stats.revenue || 0).toFixed(2)}` },
    { label: "Messages", v: stats.messages },
    { label: "Subscribers", v: stats.newsletter },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-overview">
      {items.map((it, i) => (
        <div key={i} className="card-light p-6">
          <div className="label-tag" style={{ color: "#000" }}>{it.label}</div>
          <div className="font-heading font-black text-4xl mt-2 metallic-gold">{it.v ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

function Table({ rows, cols, onDelete, testid }) {
  return (
    <div className="card-light p-4 overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-black/10">
            {cols.map(c => <th key={c} className="text-left py-3 px-2 font-heading uppercase text-xs tracking-widest text-neutral-600">{c}</th>)}
            {onDelete && <th></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-black/5 hover:bg-neutral-50">
              {cols.map(c => <td key={c} className="py-3 px-2">{String(r[c] ?? "—").slice(0,80)}</td>)}
              {onDelete && <td className="text-right"><button onClick={()=>onDelete(r.id)} className="text-red-500 hover:text-red-700" data-testid={`admin-delete-${r.id}`}><Trash2 size={14}/></button></td>}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length+1} className="text-center py-10 text-neutral-400">Empty.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function EventsAdmin({ data, reload }) {
  const [f, setF] = useState({ title:"", description:"", event_type:"camp", date:"", location:"", price:0, capacity:50, image_url:"", waiver_required:true });
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/events", { ...f, price: parseFloat(f.price), capacity: parseInt(f.capacity)||null });
      toast.success("Event created."); reload();
      setF({ title:"", description:"", event_type:"camp", date:"", location:"", price:0, capacity:50, image_url:"", waiver_required:true });
    } catch { toast.error("Create failed."); }
  };
  const del = async (id) => { if(!confirm("Delete?"))return; await api.delete(`/events/${id}`); reload(); };
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <form onSubmit={create} className="card-light p-6 space-y-3">
        <div className="font-heading uppercase font-bold">New Event</div>
        <input required placeholder="Title" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} className="input-sharp" data-testid="admin-event-title"/>
        <textarea required placeholder="Description" value={f.description} onChange={(e)=>setF({...f,description:e.target.value})} className="w-full border border-black/10 p-2" rows={2} data-testid="admin-event-desc"/>
        <select value={f.event_type} onChange={(e)=>setF({...f,event_type:e.target.value})} className="input-sharp" data-testid="admin-event-type">
          <option value="camp">Camp</option><option value="combine">Combine</option><option value="tournament">Tournament</option><option value="training">Training</option>
        </select>
        <input required type="datetime-local" value={f.date} onChange={(e)=>setF({...f,date:e.target.value})} className="input-sharp" data-testid="admin-event-date"/>
        <input required placeholder="Location" value={f.location} onChange={(e)=>setF({...f,location:e.target.value})} className="input-sharp" data-testid="admin-event-loc"/>
        <input required placeholder="Price" type="number" step="0.01" value={f.price} onChange={(e)=>setF({...f,price:e.target.value})} className="input-sharp" data-testid="admin-event-price"/>
        <input placeholder="Image URL" value={f.image_url} onChange={(e)=>setF({...f,image_url:e.target.value})} className="input-sharp" data-testid="admin-event-image"/>
        <button className="btn-gold" data-testid="admin-event-create"><Plus size={14}/>Create</button>
      </form>
      <div className="lg:col-span-2 card-light p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-black/10"><th className="text-left py-2 px-2">Title</th><th className="text-left py-2 px-2">Type</th><th className="text-left py-2 px-2">Date</th><th className="text-left py-2 px-2">$</th><th></th></tr></thead>
          <tbody>
            {data.map(ev => (
              <tr key={ev.id} className="border-b border-black/5">
                <td className="py-2 px-2">{ev.title}</td><td className="py-2 px-2">{ev.event_type}</td>
                <td className="py-2 px-2">{new Date(ev.date).toLocaleDateString()}</td>
                <td className="py-2 px-2">${ev.price}</td>
                <td className="text-right">{!ev.id.startsWith("default-") && <button onClick={()=>del(ev.id)} className="text-red-500"><Trash2 size={14}/></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CoachesAdmin({ data, reload }) {
  const [f, setF] = useState({ name:"", title:"", bio:"", photo_url:"", experience:"", certifications:"", philosophy:"" });
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coaches", { ...f, certifications: f.certifications.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success("Coach added."); reload();
      setF({ name:"", title:"", bio:"", photo_url:"", experience:"", certifications:"", philosophy:"" });
    } catch { toast.error("Failed."); }
  };
  const del = async (id) => { if(!confirm("Delete?"))return; await api.delete(`/coaches/${id}`); reload(); };
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <form onSubmit={create} className="card-light p-6 space-y-3">
        <div className="font-heading uppercase font-bold">New Coach</div>
        <input required placeholder="Name" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className="input-sharp" data-testid="admin-coach-name"/>
        <input required placeholder="Title" value={f.title} onChange={(e)=>setF({...f,title:e.target.value})} className="input-sharp" data-testid="admin-coach-title"/>
        <textarea required placeholder="Bio" value={f.bio} onChange={(e)=>setF({...f,bio:e.target.value})} className="w-full border border-black/10 p-2" rows={2} data-testid="admin-coach-bio"/>
        <input placeholder="Photo URL (or use uploader below)" value={f.photo_url} onChange={(e)=>setF({...f,photo_url:e.target.value})} className="input-sharp" data-testid="admin-coach-photo"/>
        <ImageUpload label="Or Upload Coach Photo" value={f.photo_url} onChange={(url)=>setF({...f,photo_url:url})} testid="admin-coach-photo-upload"/>
        <input placeholder="Experience" value={f.experience} onChange={(e)=>setF({...f,experience:e.target.value})} className="input-sharp" data-testid="admin-coach-exp"/>
        <input placeholder="Certifications (comma)" value={f.certifications} onChange={(e)=>setF({...f,certifications:e.target.value})} className="input-sharp" data-testid="admin-coach-certs"/>
        <input placeholder="Philosophy" value={f.philosophy} onChange={(e)=>setF({...f,philosophy:e.target.value})} className="input-sharp" data-testid="admin-coach-phil"/>
        <button className="btn-gold" data-testid="admin-coach-create"><Plus size={14}/>Create</button>
      </form>
      <div className="lg:col-span-2 card-light p-4 space-y-3">
        {data.map(c => <CoachRow key={c.id} coach={c} reload={reload} onDelete={()=>del(c.id)} />)}
      </div>
    </div>
  );
}

function CoachRow({ coach, reload, onDelete }) {
  const [busy, setBusy] = useState(false);
  const isDbCoach = !coach.id.startsWith("default-");
  const updatePhoto = async (url) => {
    if (!isDbCoach) { toast.error("Reload page after seeding — cannot patch defaults."); return; }
    setBusy(true);
    try {
      await api.patch(`/coaches/${coach.id}`, { photo_url: url });
      toast.success("Photo updated.");
      reload();
    } catch { toast.error("Update failed."); }
    setBusy(false);
  };
  return (
    <div className="flex items-start gap-4 py-3 border-b border-black/10 last:border-0" data-testid={`admin-coach-row-${coach.id}`}>
      <div className="w-20 h-24 bg-neutral-100 border border-black/10 flex-shrink-0 overflow-hidden">
        {coach.photo_url && <img src={coach.photo_url} alt={coach.name} className="w-full h-full object-cover object-top"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading uppercase font-bold">{coach.name}</div>
        <div className="text-xs text-neutral-500">{coach.title}</div>
        <div className="mt-3">
          <ImageUpload label="Replace Photo" value={coach.photo_url} onChange={updatePhoto} testid={`admin-coach-inline-${coach.id}`}/>
        </div>
        {busy && <div className="text-xs text-neutral-500 mt-1">Saving…</div>}
      </div>
      {isDbCoach && <button onClick={onDelete} className="text-red-500 flex-shrink-0" data-testid={`admin-coach-delete-${coach.id}`}><Trash2 size={16}/></button>}
    </div>
  );
}


function SigneesAdmin({ data, coaches, reload }) {
  const [f, setF] = useState({ player_name:"", class_year:"", position:"", high_school:"", college:"", coach_id:"", photo_url:"", college_logo_url:"" });
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/signees", { ...f, coach_id: f.coach_id || null });
      toast.success("Signee added."); reload();
      setF({ player_name:"", class_year:"", position:"", high_school:"", college:"", coach_id:"", photo_url:"", college_logo_url:"" });
    } catch { toast.error("Failed."); }
  };
  const del = async (id) => { if(!confirm("Delete?"))return; await api.delete(`/signees/${id}`); reload(); };
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <form onSubmit={create} className="card-light p-6 space-y-3">
        <div className="font-heading uppercase font-bold">New Signee</div>
        <input required placeholder="Player Name" value={f.player_name} onChange={(e)=>setF({...f,player_name:e.target.value})} className="input-sharp" data-testid="admin-signee-name"/>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Class Year (e.g. 2025)" value={f.class_year} onChange={(e)=>setF({...f,class_year:e.target.value})} className="input-sharp" data-testid="admin-signee-class"/>
          <input placeholder="Position" value={f.position} onChange={(e)=>setF({...f,position:e.target.value})} className="input-sharp" data-testid="admin-signee-pos"/>
        </div>
        <input placeholder="High School" value={f.high_school} onChange={(e)=>setF({...f,high_school:e.target.value})} className="input-sharp" data-testid="admin-signee-hs"/>
        <input required placeholder="College (e.g. University of Colorado)" value={f.college} onChange={(e)=>setF({...f,college:e.target.value})} className="input-sharp" data-testid="admin-signee-college"/>
        <select value={f.coach_id} onChange={(e)=>setF({...f,coach_id:e.target.value})} className="input-sharp" data-testid="admin-signee-coach">
          <option value="">Attach to Coach (optional)</option>
          {coaches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <input placeholder="Player Photo URL" value={f.photo_url} onChange={(e)=>setF({...f,photo_url:e.target.value})} className="input-sharp" data-testid="admin-signee-photo"/>
        <input placeholder="College Logo URL" value={f.college_logo_url} onChange={(e)=>setF({...f,college_logo_url:e.target.value})} className="input-sharp" data-testid="admin-signee-logo"/>
        <button className="btn-gold" data-testid="admin-signee-create"><Plus size={14}/>Add Signee</button>
      </form>
      <div className="lg:col-span-2 card-light p-4">
        {data.length === 0 && <div className="text-neutral-400 text-center py-10 text-sm">No signees yet. Add your first D1/D2/JUCO commit.</div>}
        {data.map(s => (
          <div key={s.id} className="flex justify-between items-center py-3 border-b border-black/10 last:border-0">
            <div className="flex items-center gap-3">
              {s.college_logo_url && <img src={s.college_logo_url} alt={s.college} className="w-8 h-8 object-contain"/>}
              <div>
                <div className="font-heading uppercase font-bold text-sm">{s.player_name} {s.class_year && `· ${s.class_year}`}</div>
                <div className="text-xs text-neutral-500">{s.position && `${s.position} · `}{s.college} {s.coach_id && `· ${coaches.find(c=>c.id===s.coach_id)?.name || ""}`}</div>
              </div>
            </div>
            <button onClick={()=>del(s.id)} className="text-red-500" data-testid={`admin-signee-delete-${s.id}`}><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsAdmin({ data, reload }) {
  const [f, setF] = useState({ name:"", role:"", school:"", quote:"" });
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/testimonials", f);
      toast.success("Testimonial added."); reload();
      setF({ name:"", role:"", school:"", quote:"" });
    } catch { toast.error("Failed."); }
  };
  const del = async (id) => { if(!confirm("Delete?"))return; await api.delete(`/testimonials/${id}`); reload(); };
  const isDefault = data.length > 0 && data[0].id === undefined;
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <form onSubmit={create} className="card-light p-6 space-y-3">
        <div className="font-heading uppercase font-bold">New Testimonial</div>
        <input required placeholder="Name (e.g. Marcus J.)" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} className="input-sharp" data-testid="admin-testimonial-name"/>
        <input placeholder="Role (e.g. OL, Class of 2025)" value={f.role} onChange={(e)=>setF({...f,role:e.target.value})} className="input-sharp" data-testid="admin-testimonial-role"/>
        <input placeholder="School / College (e.g. Now at SEC school)" value={f.school} onChange={(e)=>setF({...f,school:e.target.value})} className="input-sharp" data-testid="admin-testimonial-school"/>
        <textarea required placeholder="Quote" value={f.quote} onChange={(e)=>setF({...f,quote:e.target.value})} className="w-full border border-black/10 p-2" rows={4} data-testid="admin-testimonial-quote"/>
        <button className="btn-gold" data-testid="admin-testimonial-create"><Plus size={14}/>Add Testimonial</button>
      </form>
      <div className="lg:col-span-2 card-light p-4">
        {isDefault && <div className="text-xs text-neutral-500 italic mb-3">Showing default testimonials. Add your first one — they will replace the defaults.</div>}
        {data.length === 0 && <div className="text-neutral-400 text-center py-10 text-sm">No testimonials yet.</div>}
        {data.map(t => (
          <div key={t.id || t.name} className="py-3 border-b border-black/10 last:border-0">
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-heading uppercase font-bold text-sm">{t.name} {t.role && <span className="text-neutral-500 font-normal">· {t.role}</span>}</div>
                {t.school && <div className="text-xs text-neutral-500">{t.school}</div>}
                <div className="text-sm text-neutral-700 mt-2 italic">&ldquo;{t.quote}&rdquo;</div>
              </div>
              {t.id && <button onClick={()=>del(t.id)} className="text-red-500 flex-shrink-0" data-testid={`admin-testimonial-delete-${t.id}`}><Trash2 size={14}/></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function PricingAdmin({ data, reload }) {
  const [rows, setRows] = useState([]);
  const [newRow, setNewRow] = useState({ key:"", name:"", price:"", billing:"monthly", features:"", featured:false });
  const [savingKey, setSavingKey] = useState("");

  useEffect(() => {
    setRows(data.map(d => ({ ...d, features_str: (d.features||[]).join("\n") })));
  }, [data]);

  const isFromDb = rows.length > 0 && rows[0].updated_at !== undefined;

  const update = (i, key, val) => {
    const copy = [...rows];
    copy[i] = { ...copy[i], [key]: val };
    setRows(copy);
  };

  const save = async (r) => {
    setSavingKey(r.key);
    try {
      await api.post("/memberships", {
        key: r.key,
        name: r.name,
        price: parseFloat(r.price) || 0,
        billing: r.billing || "monthly",
        features: (r.features_str || "").split("\n").map(s=>s.trim()).filter(Boolean),
        featured: !!r.featured,
      });
      toast.success(`Saved ${r.name}.`);
      reload();
    } catch { toast.error("Save failed."); }
    setSavingKey("");
  };

  const del = async (key) => {
    if (!confirm(`Delete "${key}" package?`)) return;
    await api.delete(`/memberships/${key}`);
    toast.success("Deleted."); reload();
  };

  const create = async (e) => {
    e.preventDefault();
    if (!newRow.key.match(/^[a-z0-9-]+$/)) return toast.error("Key must be lowercase letters, numbers, dashes only.");
    try {
      await api.post("/memberships", {
        ...newRow,
        price: parseFloat(newRow.price) || 0,
        features: newRow.features.split("\n").map(s=>s.trim()).filter(Boolean),
      });
      toast.success("Package created."); reload();
      setNewRow({ key:"", name:"", price:"", billing:"monthly", features:"", featured:false });
    } catch { toast.error("Create failed."); }
  };

  return (
    <div className="space-y-6">
      {!isFromDb && (
        <div className="card-light p-4 text-xs text-neutral-600 italic border-l-4 border-[color:var(--gold)]">
          You are currently showing <strong>default</strong> pricing. Any edit or new package you add below will save to the database and take over.
        </div>
      )}

      <div className="grid gap-4">
        {rows.map((r, i) => (
          <div key={r.key} className="card-light p-6" data-testid={`admin-pricing-${r.key}`}>
            <div className="grid md:grid-cols-6 gap-4">
              <Cell label="Key (url-safe)" v={r.key} disabled />
              <Cell label="Display Name" v={r.name} onC={(v)=>update(i,"name",v)} testid={`admin-pricing-name-${r.key}`} />
              <Cell label="Price (USD)" v={r.price} onC={(v)=>update(i,"price",v)} testid={`admin-pricing-price-${r.key}`} />
              <div>
                <label className="label-tag" style={{ color:"#000" }}>Billing</label>
                <select value={r.billing} onChange={(e)=>update(i,"billing",e.target.value)} className="input-sharp mt-1" data-testid={`admin-pricing-billing-${r.key}`}>
                  <option value="monthly">monthly</option>
                  <option value="one-time">one-time</option>
                </select>
              </div>
              <div>
                <label className="label-tag" style={{ color:"#000" }}>Featured</label>
                <input type="checkbox" checked={!!r.featured} onChange={(e)=>update(i,"featured",e.target.checked)} className="mt-3 w-5 h-5 accent-[color:var(--gold)]" data-testid={`admin-pricing-featured-${r.key}`} />
              </div>
              <div className="flex items-end gap-2 justify-end">
                <button onClick={()=>save(r)} disabled={savingKey===r.key} className="btn-gold !py-2 !px-4" data-testid={`admin-pricing-save-${r.key}`}>{savingKey===r.key?"…":"Save"}</button>
                {r.updated_at && <button onClick={()=>del(r.key)} className="text-red-500" data-testid={`admin-pricing-delete-${r.key}`}><Trash2 size={16}/></button>}
              </div>
            </div>
            <div className="mt-4">
              <label className="label-tag" style={{ color:"#000" }}>Features (one per line)</label>
              <textarea value={r.features_str || ""} onChange={(e)=>update(i,"features_str",e.target.value)} rows={4}
                className="mt-2 w-full border border-black/15 bg-white p-3 font-mono text-xs focus:border-[color:var(--gold)] outline-none" data-testid={`admin-pricing-features-${r.key}`} />
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={create} className="card-light p-6 border-dashed border-2 border-black/20">
        <div className="font-heading uppercase font-bold mb-4">Add New Package</div>
        <div className="grid md:grid-cols-4 gap-4">
          <Cell label="Key (e.g. platinum)" v={newRow.key} onC={(v)=>setNewRow({...newRow,key:v.toLowerCase()})} testid="admin-pricing-new-key" />
          <Cell label="Display Name" v={newRow.name} onC={(v)=>setNewRow({...newRow,name:v})} testid="admin-pricing-new-name" />
          <Cell label="Price (USD)" v={newRow.price} onC={(v)=>setNewRow({...newRow,price:v})} testid="admin-pricing-new-price" />
          <div>
            <label className="label-tag" style={{ color:"#000" }}>Billing</label>
            <select value={newRow.billing} onChange={(e)=>setNewRow({...newRow,billing:e.target.value})} className="input-sharp mt-1" data-testid="admin-pricing-new-billing">
              <option value="monthly">monthly</option>
              <option value="one-time">one-time</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="label-tag" style={{ color:"#000" }}>Features (one per line)</label>
          <textarea value={newRow.features} onChange={(e)=>setNewRow({...newRow,features:e.target.value})} rows={3}
            className="mt-2 w-full border border-black/15 bg-white p-3 font-mono text-xs focus:border-[color:var(--gold)] outline-none" data-testid="admin-pricing-new-features" />
        </div>
        <div className="mt-4 flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newRow.featured} onChange={(e)=>setNewRow({...newRow,featured:e.target.checked})} className="w-4 h-4 accent-[color:var(--gold)]" data-testid="admin-pricing-new-featured"/> Featured (Most Popular)</label>
          <button className="btn-gold ml-auto" data-testid="admin-pricing-new-create"><Plus size={14}/>Add Package</button>
        </div>
      </form>
    </div>
  );
}

function Cell({ label, v, onC, disabled, testid }) {
  return (
    <div>
      <label className="label-tag" style={{ color:"#000" }}>{label}</label>
      <input disabled={disabled} value={v ?? ""} onChange={onC?(e)=>onC(e.target.value):undefined} className={`input-sharp mt-1 ${disabled?"opacity-60":""}`} data-testid={testid} />
    </div>
  );
}

